import api from './base-api';
import { CreateCampaignDto, UpdateCampaignDto, StatsBlockKey } from '@sportspredict/shared';

interface CampaignDateRange {
	start: string;
	end: string;
}

interface CampaignScopeJson {
	lobbyIds: string[];
	dateRange: CampaignDateRange;
}

interface CampaignAudienceJson {
	minTradesInScope: number;
}

interface CampaignLobbyRankConfigJson {
	amongTraders: boolean;
}

export interface CampaignConfigJson {
	scope: CampaignScopeJson;
	audience: CampaignAudienceJson;
	statsBlocks: StatsBlockKey[];
	lobbyRankConfig?: CampaignLobbyRankConfigJson;
}

interface CampaignRow {
	id: string;
	name: string;
	subject: string;
	intro_text: string | null;
	config: CampaignConfigJson;
	created_by: string | null;
	created_date: string;
	updated_date: string;
}

const campaignsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		listCampaigns: builder.query<CampaignRow[], void>({
			query: () => ({ url: 'campaigns', method: 'GET' }),
			transformResponse: (response: { campaigns: CampaignRow[] }) => response.campaigns,
			providesTags: ['Campaign' as never],
		}),

		getCampaign: builder.query<{ campaign: CampaignRow; audienceCount: number }, string>({
			query: (id) => ({ url: `campaigns/${id}`, method: 'GET' }),
			providesTags: ['Campaign' as never],
		}),

		createCampaign: builder.mutation<{ id: string }, CreateCampaignDto>({
			query: (body) => ({ url: 'campaigns', method: 'POST', body }),
			invalidatesTags: ['Campaign' as never],
		}),

		updateCampaign: builder.mutation<
			{ campaign: CampaignRow },
			{ id: string; data: UpdateCampaignDto }
		>({
			query: ({ id, data }) => ({ url: `campaigns/${id}`, method: 'PATCH', body: data }),
			invalidatesTags: ['Campaign' as never],
		}),

		deleteCampaign: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({ url: `campaigns/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Campaign' as never],
		}),

		previewCampaign: builder.mutation<{ html: string }, { id: string; userId?: string }>({
			query: ({ id, userId }) => ({
				url: `campaigns/${id}/preview`,
				method: 'POST',
				body: { userId },
			}),
		}),

		testSendCampaign: builder.mutation<{ success: boolean }, { id: string; userId: string }>({
			query: ({ id, userId }) => ({
				url: `campaigns/${id}/test`,
				method: 'POST',
				body: { userId },
			}),
		}),

		bulkSendCampaign: builder.mutation<
			{ success: boolean; sendId: string; status: string },
			string
		>({
			query: (id) => ({ url: `campaigns/${id}/send`, method: 'POST' }),
			invalidatesTags: ['Campaign' as never],
		}),
	}),
	overrideExisting: false,
});

export default campaignsApi;

export const {
	useListCampaignsQuery,
	useGetCampaignQuery,
	useCreateCampaignMutation,
	useUpdateCampaignMutation,
	useDeleteCampaignMutation,
	usePreviewCampaignMutation,
	useTestSendCampaignMutation,
	useBulkSendCampaignMutation,
} = campaignsApi;
